<template>
  <platform-menu-button
    :primary="action === 'queue'"
    :secondary="action === 'download' || action === 'send'"
    close-on-click
    class="grow"
  >
    <template #menu v-if="hasMultipleVariants">
      <v-row no-gutters class="flex-column">
        <platform-button
          v-for="{ variantCode, tooltip, reportDocument } in reportDocumentVariants"
          :key="variantCode"
          v-show="allVariantsOptionEnabled ? true : variantCode !== selectedVariant"
          v-tooltip:bottom="tooltip"
          default-case
          class="pointer-events--auto mt-1"
          :primary="action === 'queue'"
          :secondary="action === 'download' || action === 'send'"
          :icon="icon"
          :loading="pendingVariant === variantCode || (action === 'send' && reportDocument && reportDocument.sendStatus === 'PENDING')"
          :disabled="requiresFinishedReport && ((!reportDocument || !reportDocument.finishedAt) || (allVariantsOptionEnabled && notAllVariantsGenerated(variantCode)))"
          @click="emitClick(variantCode)"
        >
          {{ actionButtonText }} ({{ variantCode.toUpperCase() }})
        </platform-button>
      </v-row>
    </template>
    <platform-button
      v-tooltip:bottom="defaultActionTooltip"
      default-case
      class="grow pointer-events--auto"
      :primary="action === 'queue'"
      :secondary="action === 'download' || action === 'send'"
      :icon="icon"
      :loading="pendingVariant === selectedVariant || pendingVariant === true || (action === 'send' && latestReportDocument && latestReportDocument.sendStatus === 'PENDING')"
      :disabled="requiresFinishedReport && ((!latestReportDocument || !latestReportDocument.finishedAt) || (allVariantsOptionEnabled && notAllVariantsGenerated()))"
      @click="emitClick(allVariantsOptionEnabled ? undefined : selectedVariant)"
    >
      {{ actionButtonText }}
      <template v-if="hasMultipleVariants && selectedVariant && selectedVariant !== NullVariantCodeValue">
        ({{ allVariantsOptionEnabled ? 'All' : selectedVariant.toUpperCase() }})
      </template>
    </platform-button>
  </platform-menu-button>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { QuestionnaireVariantCode } from '@betterboards/shared/types/Platform'
import {
  getReportDocumentStatusTooltip,
  getVariantLatestReportDocument
} from '@/helpers/reportDocument'
import PlatformMenuButton from '@/components/shared/PlatformMenuButton.vue'
import { NullVariantCodeValue } from '@/helpers/questionnaire'
import { PlatformReportDocument } from '@betterboards/shared/types/Report'
import { ReportsIndividual } from '@betterboards/shared/types/Individual'

interface VariantReportDocumentInfo {
  variantCode: QuestionnaireVariantCode
  tooltip: string
  reportDocument: PlatformReportDocument | undefined
}

export default Vue.extend({
  name: 'ReportDocumentAction',
  components: {
    PlatformMenuButton
  },
  data: () => ({
    NullVariantCodeValue
  }),
  props: {
    action: { type: String as PropType<'queue' | 'download' | 'send'>, required: true },
    variants: { type: Array as PropType<QuestionnaireVariantCode[] | undefined>, required: false },
    reportDocuments: { type: Array as PropType<PlatformReportDocument[]>, required: false },
    selectedVariant: { type: String as PropType<QuestionnaireVariantCode | undefined>, required: false },
    icon: { type: String, required: true },
    pendingVariant: { type: [Boolean, String] as PropType<boolean | QuestionnaireVariantCode>, default: false },
    allVariantsOptionEnabled: { type: Boolean as PropType<boolean>, required: false },
    individuals: { type: Array as PropType<ReportsIndividual[]>, required: false }
  },
  computed: {
    hasMultipleVariants (): boolean {
      if (!this.variants) {
        return false
      }
      return this.variants.length > 1
    },
    actionButtonText (): string {
      let translationKey: string
      switch (this.action) {
        case 'queue':
          translationKey = 'analysis.generateReport.generateButton'
          break
        case 'download':
          translationKey = 'analysis.generateReport.downloadButton'
          break
        case 'send':
          translationKey = 'analysis.generateReport.sendButton'
          break
        default:
          translationKey = 'analysis.generateReport.generateButton'
      }
      return this.$t(translationKey) as string
    },
    requiresFinishedReport (): boolean {
      return this.action === 'download' || this.action === 'send'
    },
    latestReportDocument (): PlatformReportDocument | undefined {
      return getVariantLatestReportDocument(
        this.reportDocuments,
        this.selectedVariant
      )
    },
    defaultActionTooltip (): string {
      return this.getActionTooltip(this.latestReportDocument)
    },
    reportDocumentVariants (): VariantReportDocumentInfo[] {
      if (!this.variants) {
        return []
      }
      return this.variants.map((variantCode: QuestionnaireVariantCode): VariantReportDocumentInfo => {
        const reportDocument = getVariantLatestReportDocument(
          this.reportDocuments,
          variantCode
        )
        return {
          tooltip: this.getActionTooltip(reportDocument),
          variantCode,
          reportDocument
        }
      })
    }
  },
  methods: {
    getActionTooltip (reportDocument: PlatformReportDocument | undefined): string {
      return getReportDocumentStatusTooltip(reportDocument, this.action)
    },
    emitClick (variantCode: QuestionnaireVariantCode | undefined): void {
      this.$emit('click', { variantCode })
    },
    notAllVariantsGenerated (variantCode?: QuestionnaireVariantCode): boolean {
      if (!this.individuals) {
        return false
      }

      if (!variantCode) {
        return !this.individuals.every((individual) => {
          const targetVariant = individual.variantCode === NullVariantCodeValue ? null : individual.variantCode
          return individual.reports.some((report) => report.variantCode === targetVariant)
        })
      }

      return !this.individuals.every((individual) => {
        return individual.reports.some((report) => report.variantCode === variantCode)
      })
    }
  }
})
</script>
